import { onBeforeUnmount, readonly, ref } from "vue";

/**
 * QUANDO O RAIL RECOLHIDO SE ABRE — e, principalmente, quando ele volta a fechar.
 *
 * É a regra do `useSidebarHover` da V2, e mora num arquivo próprio pelo mesmo
 * motivo que lá: ela é a única parte do rail que se pode provar sem montar meia
 * aplicação, e foi ela que quebrou.
 *
 * ── O DEFEITO QUE ISTO EXISTE PARA NÃO REPETIR ────────────────────────────
 *
 * O rail escutava `focusin`/`focusout` e tratava QUALQUER foco como intenção de
 * manter o painel aberto. Só que clicar com o mouse num link ou no cabeçalho de
 * um grupo TAMBÉM dá foco ao elemento — e o foco não sai de lá até o próximo
 * clique em outro lugar. Efeito: depois de um clique no menu, tirar o mouse não
 * fechava mais nada; o painel de 240px ficava por cima do conteúdo até alguém
 * clicar fora. Na V2 isso nunca aconteceu porque lá não há foco na conta: sai o
 * mouse, fecha.
 *
 * A correção não é apagar o foco da conta — um rail que só existe sob o ponteiro
 * é um rail que não existe no teclado. É separar os dois focos que o navegador já
 * sabe distinguir: `:focus-visible` casa quando o foco veio do TECLADO e não casa
 * quando veio de um clique de mouse em link ou botão. Então Tab continua abrindo
 * o painel, e o clique deixa de segurá-lo.
 */
export function useRailReveal() {
	/** Ponteiro dentro do painel. */
	const pointer = ref(false);
	/** Foco de TECLADO dentro do painel — o de mouse não conta. */
	const focus = ref(false);

	/**
	 * OITENTA MILISSEGUNDOS DE FOLGA NA SAÍDA — a medida da V2.
	 *
	 * Sem eles, um mouse que atravessa a coluna a caminho do conteúdo abre e fecha
	 * o painel no mesmo gesto, e a tela pisca a cada trajeto diagonal. O atraso é
	 * curto demais para ser sentido como espera e longo o bastante para que roçar
	 * não conte como entrar. Na ENTRADA não há atraso: ali a intenção já foi
	 * declarada.
	 */
	const LEAVE_MS = 80;
	let leaving: ReturnType<typeof setTimeout> | undefined;

	function enter(): void {
		clearTimeout(leaving);
		pointer.value = true;
	}

	function leave(): void {
		clearTimeout(leaving);
		leaving = setTimeout(() => {
			pointer.value = false;
		}, LEAVE_MS);
	}

	/**
	 * Foi o teclado que trouxe o foco até aqui?
	 *
	 * `:focus-visible` é a pergunta certa e o navegador já a responde. Onde o
	 * seletor não existe — jsdom, navegador antigo — `matches` LANÇA, e aí a
	 * resposta de reserva é a única outra evidência que temos: um foco que chega
	 * com o ponteiro fora do painel não veio de clique nenhum.
	 */
	function byKeyboard(target: EventTarget | null): boolean {
		if (!(target instanceof Element)) return false;
		try {
			return target.matches(":focus-visible");
		} catch {
			return !pointer.value;
		}
	}

	function focusIn(event: FocusEvent): void {
		focus.value = byKeyboard(event.target);
	}

	function focusOut(): void {
		focus.value = false;
	}

	// Um timer pendente sobre um componente desmontado escreve num ref que já não
	// é de ninguém — barato de evitar, e o tipo de coisa que só aparece em teste.
	onBeforeUnmount(() => clearTimeout(leaving));

	return {
		pointer: readonly(pointer),
		focus: readonly(focus),
		enter,
		leave,
		focusIn,
		focusOut,
	};
}
